import {
  unitOf,
  zSeatId,
  type GameSummary,
  type SeatId,
  type SeatSummary,
} from '@badger-sentinel/protocol';
import { useEffect, useRef, useState } from 'react';

import { ApiError, claimSeat, releaseSeat } from '../api/games.js';
import { ImageSlot, SLOT } from '../theme/imagery.js';
import { ErrorNote } from './chrome.js';
import {
  readPendingSeat,
  storePendingSeat,
  storeSeat,
  type ClaimedSeat,
  type JoinedSession,
  type PendingSeat,
} from './session.js';

/**
 * Page 2 — Seat select (docs/frontend/pages/seat-select.md), built against
 * `docs/frontend/page-files/Seat Select.dc.html`.
 *
 * The roster: pick one of your team's cells, get the seat token everything
 * else runs on. A GM session sees one seat, GM.
 *
 * **Content, not a page.** The classification bar and the console header are
 * `LobbyScreen`'s, mounted since entry — this draws the tabs down. The roster
 * fades up in a stagger rather than appearing whole, so arriving from entry is
 * one continuous move instead of a cut into a finished screen.
 */

const EASE = 'cubic-bezier(.22,.61,.36,1)';

/**
 * Measured against the 1280 × 720 floor rather than chosen. Below the
 * classification bar, the 84px console header, tab strip, status row and the
 * 62px commit bar there are 465px, and `16 + 84 + 14 + 308 + 16` leaves ~27px of
 * slack. Grow any of them and the rank starts scrolling.
 *
 * The tile is portrait because the patch leads it: 96px of insignia over the
 * designation, seven abreast, is a rank of units rather than a table of rows.
 */
const SEAT_TILE_H = 308;
const SEAT_PATCH = 96;
const COMMAND_BAND_H = 84;

interface CellInfo {
  readonly name: string;
}

/** Everything one cell needs to draw itself, in either the band or the grid. */
interface SeatCardState {
  readonly cell: SeatId;
  readonly seat: SeatSummary;
  readonly info: CellInfo;
  /** The formation behind this cell (`unitOf`), or null where the scenario named none. */
  readonly unit: string | null;
  readonly scenario: string;
  readonly side: string;
  readonly isMine: boolean;
  /**
   * This seat is yours from before a reload — claimed, never entered, and the
   * token to take it back is in `sessionStorage`. It reads as held on the wire,
   * by your own name, and without this it drew a disabled `HELD` button.
   */
  readonly isResumable: boolean;
  readonly isTaken: boolean;
  readonly isOpen: boolean;
  /** You already hold a seat and this is not it — M5 is one seat per player. */
  readonly blocked: boolean;
  /** The claim on *this* cell is what the server just refused. */
  readonly failed: boolean;
  readonly busy: boolean;
  readonly onTake: () => void;
  readonly onRelease: () => void;
}

/**
 * The cell's name in full.
 *
 * There is no second descriptive line. The card stacks the code over the name —
 * `SDA` over `SPACE DOMAIN AWARENESS` — and once both are on screen a third
 * line listing the module's widgets ("orbital board, delta-V, RPO / Shadow /
 * KKV") restates the name in feature form. The name is the description.
 */
const CELL_INFO: Readonly<Record<SeatId, CellInfo>> = {
  CREWCOM: { name: 'Crew Commander' },
  SDA: { name: 'Space Domain Awareness' },
  MW: { name: 'Missile Warning' },
  OW: { name: 'Orbital Warfare' },
  EW: { name: 'Electromagnetic Warfare' },
  CW: { name: 'Cyber Warfare' },
  FIRES: { name: 'Joint Fires' },
  ISR: { name: 'Intelligence, Surveillance & Reconnaissance' },
  GM: { name: 'Game Master' },
};

interface MySeat {
  readonly cell: SeatId;
  readonly seatToken: string;
  readonly epoch: number;
}

export interface SeatRosterProps {
  readonly baseUrl: string;
  readonly session: JoinedSession;
  /** The polled roster, or null until the first read lands. */
  readonly game: GameSummary | null;
  readonly rosterAgeS: number | null;
  readonly isFetching: boolean;
  readonly onRefresh: () => Promise<void>;
  readonly onClaimed: (claimed: ClaimedSeat) => void;
  /** Which tab is up — owned above so the header and the tabs cannot disagree. */
  readonly tab: 'seats' | 'scenario';
  readonly onTab: (tab: 'seats' | 'scenario') => void;
}

export function SeatRoster(props: SeatRosterProps): JSX.Element {
  const { session } = props;
  const [mySeat, setMySeat] = useState<MySeat | null>(null);
  // The claim that survived a reload, if it was this game's.
  const [pending, setPending] = useState<PendingSeat | null>(() => {
    const stored = readPendingSeat();
    return stored !== null && stored.gameId === session.gameId ? stored : null;
  });
  const [actionError, setActionError] = useState<ApiError | string | null>(null);
  const [errorCell, setErrorCell] = useState<SeatId | null>(null);
  const [busyCell, setBusyCell] = useState<SeatId | null>(null);

  // The handoff drops focus to <body>, so a keyboard user arriving from entry
  // restarts at the top of the document. The roster takes it instead.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  // The roster fades up a beat after it mounts rather than appearing whole —
  // set from an effect so the first paint is the "before" state and the browser
  // has something to transition from.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setShown(true), 20);
    return () => clearTimeout(handle);
  }, []);

  const scenario = props.game?.scenario ?? session.scenario;
  const sideBranding = props.game?.sides ?? session.sides;
  // The side's own branding block, for `unitOf`. Note what is *not* read from
  // it here: `sideOf`'s accent. Team colour is identity — the header crest, the
  // console's identity row — and this screen's controls are the product's, not
  // the team's: amber acts, cyan selects, on both sides.
  const myBranding = sideBranding[session.role];

  // Don't show the other team's seats (seat-select.md: "the server currently
  // sends all 17; filter until it doesn't").
  const allSeats: readonly SeatSummary[] = props.game?.seats ?? session.seats;
  const mySeats: readonly SeatSummary[] =
    session.role === 'GM'
      ? allSeats.filter((s) => s.cell === 'GM')
      : allSeats.filter((s) => s.team === session.role);

  const orderedCells = zSeatId.options.filter((cell) => mySeats.some((s) => s.cell === cell));
  // CREWCOM commands the other seven and is read-only by rule (§5.1) — it is not
  // a peer, and a grid of eight identical boxes says it is. It gets the band
  // across the top; the specialists get the grid. A GM session has neither (its
  // one cell is GM), so both fall through to the same code with nothing in the
  // band, and the roster stops being one lonely card in an empty field.
  /**
   * **M5 is one seat per player, and this is where it is said out loud.**
   *
   * §3.2 expects a player to hold several seats in an undermanned game, and the
   * client cannot: `setMySeat` overwrites and `release` only knows the current one,
   * so a second claim dropped the first seat's token on the floor — the seat
   * stayed claimed on the server with a holder nobody could release, and
   * `ENTER AS …` entered only the last of them. Every free card kept a live
   * `TAKE SEAT` while that was true. Scoping M5 to one seat is the answer
   * `seat-select.md` asked for; leaving the control live and broken was not.
   */
  const holdsASeat = mySeat !== null || pending !== null;

  const commandCell = orderedCells.find((cell) => cell === 'CREWCOM') ?? null;
  const specialistCells = orderedCells.filter((cell) => cell !== commandCell);
  const commandState = commandCell === null ? null : stateOf(commandCell);
  // Seven is only awkward if you insist on wrapping it. Four-and-three leaves
  // either a hole or two card widths; seven abreast in one rank is even by
  // construction, and it is what an order of battle looks like — the command
  // band over its subordinate units, all on one line. Below five cells there is
  // no rank to form, so those fall back to quarter-width tiles.
  const rankColumns = specialistCells.length >= 5 ? specialistCells.length : 4;
  // The roster is a poll, not a push (seat-select.md) — count the seat just
  // claimed even if the next poll hasn't landed yet, so SEATED doesn't read as
  // broken for the several seconds between a claim and the next refetch.
  const seatedCount = mySeats.filter((s) => s.claimed || s.cell === mySeat?.cell).length;

  async function take(cell: SeatId): Promise<void> {
    setBusyCell(cell);
    setActionError(null);
    setErrorCell(null);
    try {
      // The name is typed *after* `joinGame` has already run (EntryScreen asks
      // for it on the beat after the passphrase clears), so the session the
      // server issued carries no name and neither would the seat. Passing it
      // here is the only point at which the two meet — without it
      // `session.hello.seat.displayName` is null forever and the console's
      // identity row has no operator to name.
      const response = await claimSeat(props.baseUrl, session.gameId, session.sessionToken, cell, {
        ...(session.displayName ? { displayName: session.displayName } : {}),
        // A self-reclaim, not a takeover: the previous token proves the epoch
        // is yours, so the server hands the seat straight back instead of
        // answering SEAT_TAKEN to the person already in it.
        ...(pending !== null && pending.cell === cell ? { reclaimToken: pending.seatToken } : {}),
      });
      const claimed: MySeat = {
        cell,
        seatToken: response.seatToken,
        epoch: response.seat.epoch,
      };
      setMySeat(claimed);
      // **Stored on the claim, not on `ENTER`.** The server holds the seat from
      // this moment on its 120 s grace window; a reload before the commit used
      // to leave the player locked out of their own cell for the rest of it.
      const record: PendingSeat = { gameId: session.gameId, ...claimed };
      storePendingSeat(record);
      setPending(record);
    } catch (error) {
      setActionError(error instanceof ApiError ? error : 'CLAIM FAILED');
      setErrorCell(cell);
      // A reclaim the server refused is a seat somebody else now holds: drop the
      // stored token rather than offering RESUME on a seat that is gone.
      if (error instanceof ApiError && error.status === 409 && pending?.cell === cell) {
        storePendingSeat(null);
        setPending(null);
      }
      // seat-select.md: "claimed: false is not a guarantee... re-read it after any 409."
      await props.onRefresh();
    } finally {
      setBusyCell(null);
    }
  }

  async function release(): Promise<void> {
    const held = mySeat;
    if (!held) return;
    setBusyCell(held.cell);
    setActionError(null);
    setErrorCell(null);
    try {
      await releaseSeat(props.baseUrl, session.gameId, held.seatToken, held.cell);
    } catch (error) {
      setActionError(error instanceof ApiError ? error : 'RELEASE FAILED');
      setErrorCell(held.cell);
    } finally {
      // Local state clears either way. A release the server refused is a token
      // that is already dead to it, and holding a dead one on screen is the
      // stranding this screen exists to stop.
      setMySeat(null);
      storePendingSeat(null);
      setPending(null);
      setBusyCell(null);
      await props.onRefresh();
    }
  }

  function enterGame(): void {
    if (!mySeat) return;
    // Branding and scenario ride across the claim because nothing past this
    // point can ask for them again — see `ClaimedSeat`.
    const claimed: ClaimedSeat = {
      gameId: session.gameId,
      seatToken: mySeat.seatToken,
      cell: mySeat.cell,
      scenario: session.scenario,
      sides: session.sides,
    };
    storeSeat(claimed);
    // The console owns the seat from here; the roster's copy would only be a
    // second record of the same claim, going stale.
    storePendingSeat(null);
    props.onClaimed(claimed);
  }

  function stateOf(cell: SeatId): SeatCardState | null {
    const seat = mySeats.find((s) => s.cell === cell);
    if (!seat) return null;
    const isMine = mySeat?.cell === cell;
    const isResumable = !isMine && mySeat === null && pending?.cell === cell;
    return {
      cell,
      seat,
      isMine,
      isResumable,
      isTaken: seat.claimed && !isMine && !isResumable,
      isOpen: !seat.claimed && !isMine && !isResumable,
      blocked: holdsASeat && !isMine && !isResumable,
      failed: errorCell === cell,
      info: CELL_INFO[cell],
      unit: unitOf(myBranding, cell),
      scenario,
      side: session.role,
      busy: busyCell === cell,
      onTake: () => void take(cell),
      onRelease: () => void release(),
    };
  }

  /** Each row of the entrance comes up just behind the one above it. */
  /**
   * The roster forms rather than appears: chrome first, then the command band,
   * then the rank one unit at a time outward from it. 22px of travel and a 55ms
   * step make that legible as a sequence instead of a shimmer — at seven tiles
   * the last one lands about 380ms after the band, which is a beat, not a wait.
   * `prefers-reduced-motion` clamps all of it to nothing via `tokens.css`,
   * because this is a CSS transition and not a timer.
   */
  function rise(order: number): React.CSSProperties {
    return {
      transform: shown ? 'none' : 'translate3d(0,22px,0)',
      opacity: shown ? 1 : 0,
      transition: `transform .52s ${EASE},opacity .4s ${EASE}`,
      transitionDelay: `${order * 55}ms`,
    };
  }

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}
    >
      <div
        style={{
          flex: 'none',
          height: 39,
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          padding: '0 20px',
          ...rise(0),
        }}
      >
        {(['seats', 'scenario'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => props.onTab(key)}
            style={{
              flex: 'none',
              padding: '0 18px',
              background: 'transparent',
              border: 0,
              borderBottom: `2px solid ${props.tab === key ? 'var(--primary)' : 'transparent'}`,
              color: props.tab === key ? 'var(--text)' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: 11.5,
              letterSpacing: '.10em',
              textTransform: 'uppercase',
            }}
          >
            {key === 'seats' ? 'Select seat' : 'Scenario'}
          </button>
        ))}
      </div>

      {/* The lobby's masthead, in the console's own status-row material: a 44px
          chrome band under a strong rule, a `.14em` label over a mono readout.
          It carries only what the console header above does not — the header
          already says session, scenario and side, and the copy rules say a thing
          once per screen. So: how many seats are filled, and how old the number
          is. */}
      <div
        style={{
          flex: 'none',
          height: 44,
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border-strong)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '0 20px',
          ...rise(1),
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
              fontSize: 11,
              lineHeight: 1,
              letterSpacing: '.14em',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
            }}
          >
            Seated
          </div>
          <div
            style={{
              fontFamily: 'var(--font-data)',
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1,
              letterSpacing: '.02em',
              marginTop: 4,
            }}
          >
            {String(seatedCount).padStart(2, '0')}
            <span style={{ color: 'var(--text-disabled)' }}>
              {' / '}
              {String(mySeats.length).padStart(2, '0')}
            </span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {props.rosterAgeS !== null && (
          <span
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 10,
              letterSpacing: '.10em',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
            }}
          >
            roster · {props.rosterAgeS}s ago
          </span>
        )}
        <button
          type="button"
          data-testid="refresh-roster"
          onClick={() => void props.onRefresh()}
          disabled={props.isFetching}
          style={{
            flex: 'none',
            height: 28,
            background: 'transparent',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-2)',
            padding: '0 10px',
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            letterSpacing: '.10em',
            textTransform: 'uppercase',
          }}
        >
          {props.isFetching ? '…' : 'Refresh'}
        </button>
      </div>

      {props.tab === 'seats' ? (
        // The roster stands on the instrument's own ground rather than on the
        // page: `--canvas` (a faint 42px graticule in dark, daylight in light)
        // and the corner brackets, which are canvas furniture and appear exactly
        // once per screen. They sit on this wrapper rather than on the scroller
        // so they stay pinned to the bezel instead of sliding with content.
        <div
          data-mat="ticks"
          style={{ flex: 1, minHeight: 0, background: 'var(--canvas)', overflow: 'hidden' }}
        >
          <div style={{ height: '100%', overflow: 'auto', padding: '16px 20px' }}>
            <div data-testid="seat-picker">
              {commandState && (
                <div style={{ marginBottom: 14, ...rise(3) }}>
                  <CommandBand {...commandState} />
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${rankColumns},minmax(0,1fr))`,
                  gap: 12,
                }}
              >
                {specialistCells.map((cell, i) => {
                  const state = stateOf(cell);
                  if (!state) return null;
                  // The rank forms outward from the band, one unit at a time.
                  return (
                    <div key={cell} style={rise(4 + i)}>
                      <SeatTile {...state} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ScenarioTab game={props.game} scenario={scenario} style={rise(2)} />
      )}

      {/* The commit is the screen's one act, so it sits on its own bar under the
          roster rather than inside the grid as an eighth tile. It stays visible
          while disabled — amber at .45 per the design system — because a control
          that appears only once you have already done the thing never taught
          anyone what finishing this screen looks like. */}
      {props.tab === 'seats' && (
        <div
          style={{
            flex: 'none',
            // 62 is the design's bar and the number the 465px budget above was
            // measured against. It grows only for a refusal, and downward from
            // the bottom edge — the scroller loses height, the tiles keep their
            // positions, and nothing moves out from under the pointer.
            minHeight: 62,
            background: 'var(--surface-2)',
            borderTop: '1px solid var(--border-strong)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '8px 20px',
          }}
        >
          {/* A refusal belongs beside the act that was refused. Above the grid
              it was both far from the tile that failed and, being inside the
              scroller, it pushed the whole rank down as it appeared. The tile
              itself carries the red edge (`edgeOf`); this carries the sentence. */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <ErrorNote error={actionError} />
          </div>
          <button
            type="button"
            data-testid="enter-game"
            disabled={!mySeat}
            onClick={enterGame}
            style={{
              flex: 'none',
              height: 44,
              minWidth: 240,
              padding: '0 20px',
              background: 'var(--primary)',
              border: '1px solid var(--primary)',
              color: 'var(--primary-on)',
              opacity: mySeat ? 1 : 0.45,
              fontFamily: 'var(--font-ui)',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {mySeat
                ? `Enter as ${mySeat.cell}`
                : pending
                  ? `Resume ${pending.cell}`
                  : 'Take a seat'}
            </span>
            <span style={{ flex: 'none' }}>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Clamp to `n` lines with an ellipsis rather than a hard crop.
 *
 * `INTELLIGENCE, SURVEILLANCE & RECONNAISSANCE` and
 * `ELECTROMAGNETIC COUNTERMEASURES REGIMENT` are the long ones, and a fixed
 * height with `overflow: hidden` guillotines them mid-word — which reads as a
 * rendering fault rather than as an abbreviation.
 */
function clampLines(n: number): React.CSSProperties {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: n,
    overflow: 'hidden',
  };
}

/**
 * The seat patch in its frame.
 *
 * The art is inset off the frame rather than bleeding into it. A patch is a
 * piece of insignia with an outline; a border drawn hard against that outline
 * reads as a crop of the cloth instead of a mount for it. The inset is padding
 * on the frame, so the box keeps its exact footprint whether or not the art
 * exists — nine of seventeen patches do not, and nothing may move when they
 * arrive.
 */
function PatchBox(props: {
  cell: SeatId;
  side: string;
  scenario: string;
  size: number;
  /** The seat you hold. Frames the cloth in cyan; nothing about it moves. */
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        flex: 'none',
        boxSizing: 'border-box',
        width: props.size,
        height: props.size,
        padding: Math.round(props.size / 14),
        background: 'var(--surface-3)',
        border: `1px solid ${props.emphasis === true ? 'var(--accent-border)' : 'var(--border)'}`,
      }}
    >
      <ImageSlot slot={SLOT.seatPatch(props.cell, props.side)} scenario={props.scenario} compact />
    </div>
  );
}

/**
 * Three states, not two (seat-select.md): free · held+connected · held but
 * disconnected (the 120s grace window — it looks free, it isn't). Carried in a
 * word as well as a colour, so deleting the hue leaves the reading intact.
 */
function StatusChip(props: SeatCardState): JSX.Element {
  const { seat } = props;
  return (
    <span
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'var(--font-data)',
        fontSize: 10,
        letterSpacing: '.10em',
        textTransform: 'uppercase',
        color:
          props.isMine || props.isResumable
            ? 'var(--accent)'
            : props.isTaken
              ? dropped(seat)
                ? 'var(--warn)'
                : 'var(--text-muted)'
              : 'var(--ok)',
      }}
    >
      <span style={{ width: 6, height: 6, background: 'currentColor', display: 'inline-block' }} />
      {props.isMine || props.isResumable
        ? 'you'
        : props.isTaken
          ? dropped(seat)
            ? 'reconnecting'
            : 'held'
          : 'open'}
    </span>
  );
}

/**
 * Who holds it — the name only. The reconnecting half of the state is the
 * chip's job and the chip is always beside this; saying it twice is the copy
 * rules' "say a thing once per screen" broken inside one card.
 */
/**
 * Held, and the socket has gone — the 120 s grace window the design draws as
 * the third seat state.
 *
 * `connected` alone is not that state: it is false for **every** seat on this
 * screen, because the socket needs the seat token this screen exists to obtain.
 * So a peer who claimed a seat thirty seconds ago and is sitting reading their
 * briefing read as `reconnecting`, in amber — and so did every other claimed
 * seat in the roster, permanently. `everConnected` is what separates a player
 * who dropped from one who has not arrived yet.
 */
function dropped(seat: SeatSummary): boolean {
  return !seat.connected && seat.everConnected;
}

/**
 * The card's leading edge, which is the whole of what it says at a distance.
 *
 * Red is the tile the server just refused — a click that failed has to be
 * legible on the thing that was clicked, not only in the bar under the rank.
 */
function edgeOf(props: SeatCardState): string {
  if (props.isMine || props.isResumable) return 'var(--accent-border)';
  if (props.failed) return 'var(--red)';
  if (props.isTaken && dropped(props.seat)) return 'var(--warn)';
  return 'var(--border-strong)';
}

function holderLabelOf(props: SeatCardState): string | null {
  if (!props.isTaken) return null;
  return props.seat.displayName ?? 'occupied';
}

/** The claim control, in whichever of its three states this cell is in. */
function SeatAction(props: SeatCardState & { compact?: boolean }): JSX.Element | null {
  const shared = {
    fontFamily: 'var(--font-ui)',
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
  } as const;

  if (props.blocked) {
    return (
      <button
        type="button"
        data-testid={`seat-${props.cell}`}
        disabled
        style={{
          ...shared,
          fontWeight: 400,
          fontSize: 10,
          flex: props.compact ? 'none' : 1,
          height: props.compact ? 30 : undefined,
          background: 'transparent',
          border: 0,
          color: 'var(--text-disabled)',
          padding: '0 12px',
        }}
      >
        Release first
      </button>
    );
  }

  if (props.isOpen || props.isResumable) {
    return (
      <button
        type="button"
        data-testid={`seat-${props.cell}`}
        disabled={props.busy}
        onClick={props.onTake}
        style={{
          ...shared,
          flex: props.compact ? 'none' : 1,
          height: props.compact ? 30 : undefined,
          background: 'transparent',
          border: props.compact ? '1px solid var(--primary)' : 0,
          color: 'var(--primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '0 12px',
        }}
      >
        <span>{props.busy ? 'Claiming…' : props.isResumable ? 'Resume seat' : 'Take seat'}</span>
        <span>→</span>
      </button>
    );
  }

  if (props.isMine) {
    return (
      <button
        type="button"
        data-testid={`release-${props.cell}`}
        disabled={props.busy}
        onClick={props.onRelease}
        style={{
          ...shared,
          fontWeight: 400,
          fontSize: 10,
          flex: props.compact ? 'none' : 1,
          height: props.compact ? 30 : undefined,
          background: 'transparent',
          border: props.compact ? '1px solid var(--border-strong)' : 0,
          color: 'var(--text-2)',
          padding: '0 12px',
        }}
      >
        {props.busy ? 'Releasing…' : 'Release'}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={`seat-${props.cell}`}
      disabled
      style={{
        ...shared,
        fontWeight: 400,
        fontSize: 10,
        flex: props.compact ? 'none' : 1,
        height: props.compact ? 30 : undefined,
        background: 'transparent',
        // No border when compact: a disabled control that still looks like a
        // button is a control people keep trying to press.
        border: 0,
        color: 'var(--text-disabled)',
        padding: '0 12px',
      }}
    >
      Held
    </button>
  );
}

/**
 * CREWCOM, across the top.
 *
 * It commands the other seven and is read-only by rule (§5.1); drawing it as a
 * ninth identical box is the thing that made this screen read as a workspace
 * picker rather than a task organisation. A band, not a card — the shape says
 * "above", which is what the rule already enforces.
 */
function CommandBand(props: SeatCardState): JSX.Element {
  const heldForS =
    props.seat.heldForMs !== null ? Math.max(0, Math.round(props.seat.heldForMs / 1000)) : null;
  const holderLabel = holderLabelOf(props);

  return (
    <div
      style={{
        height: COMMAND_BAND_H,
        background: props.isMine || props.isResumable ? 'var(--accent-tint)' : 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `2px solid ${edgeOf(props)}`,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 14px',
        overflow: 'hidden',
      }}
    >
      <PatchBox
        cell={props.cell}
        side={props.side}
        scenario={props.scenario}
        size={64}
        emphasis={props.isMine || props.isResumable}
      />
      <div style={{ minWidth: 0, flex: 'none' }}>
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1,
            letterSpacing: '.03em',
            textTransform: 'uppercase',
            color: props.isMine || props.isResumable ? 'var(--accent)' : 'var(--text)',
          }}
        >
          {props.cell}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 11,
            lineHeight: 1,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginTop: 6,
          }}
        >
          {props.info.name}
        </div>
      </div>
      <div style={{ width: 1, height: 44, background: 'var(--rule)', flex: 'none' }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        {props.unit !== null && (
          <div
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              lineHeight: 1.2,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {props.unit}
          </div>
        )}
      </div>
      {holderLabel && (
        <div
          style={{
            flex: 'none',
            maxWidth: 200,
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            letterSpacing: '.08em',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {holderLabel}
          {heldForS !== null ? ` · ${heldForS}s` : ''}
        </div>
      )}
      <StatusChip {...props} />
      <SeatAction {...props} compact />
    </div>
  );
}

/**
 * One unit in the rank.
 *
 * Portrait, and the patch leads: 96px of insignia over the designation, seven
 * abreast. The seat you are choosing is a *unit*, and the cloth is the fastest
 * thing in the product to tell one from another across a room — which is the
 * distance this is read at.
 *
 * The held seat frames its patch in cyan. Deliberately a colour change and not a
 * size one: the design system's Nothing Moves On Hover rule means a control that
 * grows under the pointer is a control that is harder to hit, and this screen is
 * used against a clock.
 */
function SeatTile(props: SeatCardState): JSX.Element {
  const { seat } = props;
  const heldForS = seat.heldForMs !== null ? Math.max(0, Math.round(seat.heldForMs / 1000)) : null;
  const holderLabel = holderLabelOf(props);

  return (
    <div
      style={{
        height: SEAT_TILE_H,
        boxSizing: 'border-box',
        background: props.isMine || props.isResumable ? 'var(--accent-tint)' : 'var(--surface)',
        border: '1px solid var(--border)',
        borderTop: `2px solid ${edgeOf(props)}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '12px 10px 0',
        }}
      >
        <PatchBox
          cell={props.cell}
          side={props.side}
          scenario={props.scenario}
          size={SEAT_PATCH}
          emphasis={props.isMine || props.isResumable}
        />
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1,
            letterSpacing: '.03em',
            textTransform: 'uppercase',
            color: props.isMine || props.isResumable ? 'var(--accent)' : 'var(--text)',
            marginTop: 12,
          }}
        >
          {props.cell}
        </div>
        {/* A name identical to its own code would print twice and leave a hole
            in the rank; no shipped cell does that now, and this keeps the next
            one from reintroducing it. The slot holds its height either way, so
            the units and chips below stay on one line across all seven. */}
        <div
          style={{
            ...clampLines(3),
            width: '100%',
            height: 43,
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 11,
            lineHeight: 1.3,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginTop: 7,
          }}
        >
          {props.info.name.toUpperCase() === props.cell ? '' : props.info.name}
        </div>
        {props.unit !== null && (
          <div
            style={{
              ...clampLines(3),
              width: '100%',
              height: 39,
              fontFamily: 'var(--font-data)',
              fontSize: 10,
              lineHeight: 1.3,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: 'var(--text-3)',
              marginTop: 6,
            }}
          >
            {props.unit}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <StatusChip {...props} />
        {holderLabel !== null && (
          <div
            style={{
              width: '100%',
              fontFamily: 'var(--font-data)',
              fontSize: 10,
              lineHeight: 1.3,
              letterSpacing: '.08em',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              marginTop: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {holderLabel}
            {heldForS !== null ? ` · ${heldForS}s` : ''}
          </div>
        )}
        <div style={{ flex: 'none', height: 8 }} />
      </div>
      <div
        style={{
          flex: 'none',
          height: 34,
          borderTop: '1px solid var(--rule, var(--border))',
          display: 'flex',
        }}
      >
        <SeatAction {...props} />
      </div>
    </div>
  );
}

function ScenarioTab(props: {
  game: GameSummary | null;
  scenario: string;
  style?: React.CSSProperties;
}): JSX.Element {
  const g = props.game;
  const params: readonly [string, string][] = g
    ? [
        ['Scenario', g.scenario || props.scenario || '—'],
        ['Timing profile', g.timingProfile],
        ['Max turns', g.phase.maxTurns > 0 ? String(g.phase.maxTurns) : '—'],
        // No `Multi-seating` row. It was the only client reference to
        // `g.config.multiSeating`, and it read "Allowed" on a screen that cannot
        // do it — a parameter the player is told about and the product then
        // refuses. It comes back when the roster can hold two seats.
        ['CREWCOM sees team drafts', g.config.crewcomSeesDrafts ? 'Yes' : 'No'],
        ['Objective progress shown', g.config.showObjectiveProgress ? 'Yes' : 'No'],
      ]
    : [];

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px', ...props.style }}>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 700,
          fontSize: 26,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          marginBottom: 14,
        }}
      >
        Scenario
      </div>
      <div
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', maxWidth: 460 }}
      >
        <div
          style={{
            height: 33,
            background: 'var(--surface-2)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px',
            fontFamily: 'var(--font-ui)',
            fontWeight: 700,
            fontSize: 10.5,
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--text-2)',
          }}
        >
          Parameters
        </div>
        <div style={{ padding: '6px 14px 10px' }}>
          {g ? (
            params.map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  padding: '6px 0',
                  borderBottom: '1px solid var(--rule-faint, var(--border))',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 10,
                    letterSpacing: '.16em',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                  }}
                >
                  {k}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--text)',
                    textTransform: 'uppercase',
                  }}
                >
                  {v}
                </span>
              </div>
            ))
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, padding: '8px 0' }}>Loading…</p>
          )}
        </div>
      </div>
    </div>
  );
}
